// Presentation-only compatibility for the deployed v2 contract. No value,
// grade, threshold, confidence, trend, readiness or permission is calculated.
import { reportPhotoSource } from "./reportMedia";
const object = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const values = (value) =>
  Array.isArray(value) ? value : object(value) ? Object.values(value) : [];
const own = (value, key) =>
  Object.prototype.hasOwnProperty.call(value || {}, key);

export function normalizeMobilityReport(document) {
  if (
    document?.schema_version !== "mobility-report.v2" ||
    !Array.isArray(document.presentation?.sections) ||
    !document.presentation.sections.some(
      (section) =>
        object(section) && own(section, "key") && !own(section, "type"),
    )
  )
    return document;
  if (
    [
      "context",
      "person",
      "assessment",
      "capabilities",
      "metrics",
      "explanations",
      "charts",
    ].some((key) => !object(document[key]))
  )
    return document;
  const presentation = document.presentation;
  const domains = values(document.domains);
  const rawCharts = document.charts;
  const charts = [];
  if (object(rawCharts)) {
    for (const kind of ["domains", "metrics", "fim_items"]) {
      for (const [id, chart] of Object.entries(rawCharts[kind] || {})) {
        if (!object(chart)) continue;
        const metric = document.metrics?.[chart.metric_id ?? id];
        const domain = domains.find(
          (entry) => entry.id === (chart.domain_id ?? id),
        );
        charts.push({
          ...chart,
          id: chart.id ?? `${kind}:${id}`,
          label: chart.label ?? metric?.label ?? domain?.label,
          display_unit: chart.display_unit ?? chart.unit,
          bands: Array.isArray(chart.bands) ? chart.bands : [],
          points: values(chart.points).map((point) => {
            const matching = values(metric?.history).find(
              (entry) => entry.date === point.date,
            );
            return {
              ...point,
              value: own(point, "value") ? point.value : point.index,
              display_value: own(point, "display_value")
                ? point.display_value
                : kind === "domains"
                  ? point.tier_label ?? point.tier
                  : undefined,
              grade: point.grade ?? point.tier,
              grade_label:
                point.grade_label ??
                point.tier_label ??
                matching?.grade_label ??
                (kind === "domains" ? point.tier : undefined),
              source: point.source ?? matching?.source,
            };
          }),
        });
      }
    }
    for (const kind of ["fim_total", "readiness"])
      if (object(rawCharts[kind]))
        charts.push({ ...rawCharts[kind], id: rawCharts[kind].id ?? kind });
  }
  const explanations = [];
  const defined = document.explanations?.how_defined;
  if (object(defined))
    explanations.push({
      ...defined,
      id: "how_defined",
      sections: [
        defined.subtitle,
        ...values(defined.blocks ?? defined.sections),
      ].filter((value) => value != null),
    });
  if (document.explanations?.metric_note)
    explanations.push({
      id: "metric_note",
      body: document.explanations.metric_note,
    });
  for (const [id, body] of Object.entries(
    document.explanations?.domain_reasons || {},
  ))
    explanations.push({
      id: `domain:${id}`,
      title: domains.find((domain) => domain.id === id)?.label,
      body,
    });
  if (document.explanations?.readiness_scenarios)
    explanations.push({
      id: "readiness_scenarios",
      sections: document.explanations.readiness_scenarios,
    });
  const metrics = object(document.metrics)
    ? Object.fromEntries(
        Object.entries(document.metrics).map(([id, metric]) => {
          if (!object(metric)) return [id, metric];
          const explanation = metric.explanation;
          return [
            id,
            {
              ...metric,
              score_note: metric.score_note ?? metric.unscored_note,
              rationale: metric.rationale ?? metric.relevance?.text,
              chart: charts.find((chart) => chart.metric_id === id),
              how_calculated:
                metric.how_calculated ??
                (object(explanation)
                  ? {
                      title: explanation.title,
                      sections: [
                        explanation.subtitle,
                        explanation.current_value,
                        explanation.direction_text,
                        ...values(explanation.threshold_lines),
                        explanation.unscored_note,
                        explanation.captured_by,
                        explanation.freshness_text,
                        explanation.inputs_text,
                      ].filter((value) => value != null),
                    }
                  : explanation),
            },
          ];
        }),
      )
    : document.metrics;
  const capabilities = { ...document.capabilities };
  for (const [target, source] of Object.entries({
    can_share: "share",
    can_print: "print",
    can_view_history: "history",
    can_edit_fim: "edit_fim",
    can_edit_remarks: "edit_remarks",
    can_change_activity: "change_activity",
  }))
    if (!own(capabilities, target) && own(capabilities, source))
      capabilities[target] = capabilities[source];
  // Deployed section keys -> the client's section vocabulary. Several keys can
  // name one client section (Primary Care's "domain_overview" is the summary
  // table; Rehab's "fim_summary" + "fim_items" are the FIM section), which is
  // then shown once, where it first appears.
  const aliases = {
    overall_assessment: "headline",
    trends: "charts",
    domain_overview: "summary_table",
    fim_summary: "fim",
    fim_items: "fim",
  };
  const seen = new Set();
  const sections = presentation.sections
    .filter((section) => section?.key !== "header")
    .map((section) =>
      object(section)
        ? {
            ...section,
            type: section.type ?? aliases[section.key] ?? section.key,
          }
        : section,
    )
    .filter((section) => {
      if (!object(section) || section.visible === false) return true;
      if (seen.has(section.type)) return false;
      seen.add(section.type);
      return true;
    });
  const editTargets = values(document.edit_targets).map((target) => ({
    ...target,
    editable_item_ids: target.editable_item_ids ?? target.fim_items,
    editable_fields:
      target.editable_fields ??
      (Array.isArray(target.remarks)
        ? target.remarks.map((kind) => `remarks.${kind}`)
        : undefined),
  }));
  const remarks = object(document.remarks)
    ? {
        ...document.remarks,
        ...Object.fromEntries(
          ["staff", "person"]
            .filter((kind) => object(document.remarks[kind]))
            .map((kind) => [
              kind,
              {
                ...document.remarks[kind],
                assessment_id:
                  document.remarks[kind].assessment_id ??
                  document.remarks.assessment_id,
              },
            ]),
        ),
      }
    : document.remarks;
  return {
    ...document,
    presentation: {
      ...presentation,
      title: presentation.title ?? presentation.report_title,
      footer:
        presentation.footer ??
        (presentation.labels?.disclaimer
          ? {
              title: presentation.labels.disclaimer_title,
              text: presentation.labels.disclaimer,
            }
          : undefined),
      sections,
    },
    capabilities,
    edit_targets: editTargets,
    metrics,
    charts,
    explanations,
    remarks,
    person: {
      ...document.person,
      external_id_label:
        document.person?.external_id_label ??
        document.person?.terminology?.id_label,
      avatar_url: reportPhotoSource(document.person),
      injury_label:
        document.person?.injury_label ?? document.person?.injury?.label,
      equipment_used:
        document.person?.equipment_used ?? document.assessment?.equipment,
    },
    assessment: {
      ...document.assessment,
      date_label:
        document.assessment?.date_label ??
        document.assessment?.last_assessment_date_label,
      assessed_by:
        document.assessment?.assessed_by ?? document.assessment?.assessor,
    },
    domains: domains.map((domain) => ({
      ...domain,
      explanation: domain.explanation ?? domain.why_flagged ?? domain.summary,
      add_tests: values(domain.add_tests).map((test) =>
        object(test) ? { ...test, label: test.label ?? test.test_label } : test,
      ),
    })),
    summary_table: { labels: presentation.labels, rows: domains },
  };
}
